import { EVIDENCE_ACK_NOTE } from './evidenceReadiness.js';

/**
 * Follow-up tags on approve (§4.3, owner-approved 2026-07-30).
 *
 * The policy is "approve anything good enough to put in front of a candidate today; polish is a later
 * pass." These are the classes of polish the programme has already **measured** on real items, so the
 * reviewer can record one without holding the item back.
 *
 * **What the UI must NOT claim.** §4.3 proposed telling the reviewer "Tagged. This opens an
 * improvement item and does not affect release." Nothing in this platform creates an improvement item
 * today, so that copy was replaced by the owner with the honest version: *"Recorded on the item and
 * will be handled later, potentially after release."* Do not restore the stronger wording until
 * something actually consumes these tags — a reviewer who is told work was created and sees nothing
 * happen stops tapping, which is exactly how eight rating signals ended up sitting in the database
 * producing no work item, no register row and no ledger row.
 *
 * **Where the tags actually land, verified 2026-07-30.** They ride the `notes` argument of
 * `approveItem(id, notes?)` into `author-review-events.notes` and `author-audit.details.notes`. They
 * are **not visible on any reviewer-facing screen**: the review-event lists filter to comment events
 * (`WorkQueueDashboard.tsx` `isCommentEvent`), nothing posts an approve to the Slack decision desk
 * (`queue.service.ts` mirrors comments, annotations and ratings only), and they surface only in an
 * audit-log cell truncated at `max-w-[200px]` behind `fromStatus`/`toStatus`
 * (`AuditLogPage.tsx`), plus the optional daily digest — which clips `details.notes` to 70
 * characters, and `EVIDENCE_ACK_NOTE` alone is 79, so on a combined approve the tag line reaches no
 * reader at all (`services/daily-report/src/format.mjs`). The copy above is honest about that;
 * making the tags reach a human is a separate owner decision, and is why the order below is left
 * canonical rather than tuned for one lossy consumer.
 */
export const FOLLOW_UP_TAGS = [
  'Thin explanation',
  'Wrong-answer feedback gives it away',
  'Formula not in display style',
  'Option style',
  'Copied too closely',
  'Weak distractor',
  'Answer findable without the subject',
] as const;

export type FollowUpTag = (typeof FOLLOW_UP_TAGS)[number];

/** The reviewer-facing promise. One string, so the UI cannot drift from what this actually does. */
export const FOLLOW_UP_TAG_EFFECT = 'Recorded on the item and will be handled later, potentially after release.';

/**
 * THE single place an approve's `notes` string is composed.
 *
 * Two independent things can need recording on one approve — the missing-evidence acknowledgement and
 * any follow-up tags — and both travel as free text through the one `notes` parameter the API already
 * accepts. Composing them here keeps every approve path (plain, with-edit, review page, queue row)
 * from inventing its own format, and is the one function to change if either ever becomes a real
 * stored field. Returns `undefined` when there is nothing to record, so an ordinary approve reaches
 * the API in exactly the call it made before any of this existed.
 */
export function buildApproveNotes({
  tags,
  acknowledgeEvidence,
}: {
  tags: readonly string[];
  acknowledgeEvidence: boolean;
}): string | undefined {
  // Vocabulary order, not click order: the same set of tags must produce the same string, or the
  // stored notes cannot be compared across items.
  const recognised = FOLLOW_UP_TAGS.filter((tag) => tags.includes(tag));
  const lines = [
    ...(acknowledgeEvidence ? [EVIDENCE_ACK_NOTE] : []),
    ...(recognised.length > 0 ? [`Follow-up, does not affect release: ${recognised.join('; ')}`] : []),
  ];
  return lines.length > 0 ? lines.join('\n') : undefined;
}
